<template>
  <div class="console-page">
    <div class="console-page__header">
      <div>
        <h1 class="console-page__title">Submit Request</h1>
        <p class="console-page__placeholder">
          Select a workspace, choose the input shape, and submit a request for planning.
        </p>
        <ConsolePollingStatus
          :active="workspacePollingActive"
          :busy="workspacePollBusy"
          idle-label="Watching workspace readiness every 3s"
          busy-label="Refreshing workspace choices..."
          tone="pending"
        />
      </div>

      <div class="console-page__actions">
        <router-link :to="{ name: 'ConsoleJobs' }" class="button button--ghost">
          Back to Runs
        </router-link>
      </div>
    </div>

    <section class="console-panel">
      <ConsoleListState
        :loading="loading"
        :error="error"
        :is-empty="false"
        loading-message="Loading workspaces..."
      >
        <div v-if="workspaceOptions.length" class="console-signal-strip job-create__summary-strip">
          <button
            type="button"
            class="console-signal console-signal--button"
            :class="selectedWorkspaceToneClass"
            aria-controls="job-create-workspace-section"
            @click="openWorkspaceSection"
          >
            <span class="console-signal__label">Workspace</span>
            <span class="console-signal__value job-create__signal-value">{{ selectedWorkspaceLabel }}</span>
            <span class="console-signal__note">{{ selectedWorkspaceNote }}</span>
          </button>
          <button
            type="button"
            class="console-signal console-signal--button console-signal--active"
            aria-controls="job-create-input-section"
            @click="openInputSection"
          >
            <span class="console-signal__label">Input Mode</span>
            <span class="console-signal__value job-create__signal-value">{{ submissionModeLabel }}</span>
            <span class="console-signal__note">Choose freeform instructions or a GitHub issue-backed plan input.</span>
          </button>
          <button
            type="button"
            class="console-signal console-signal--button"
            :class="selectedRuntime === 'claude-code' ? 'console-signal--pending' : 'console-signal--active'"
            aria-controls="job-create-runtime-section"
            @click="openRuntimeSection"
          >
            <span class="console-signal__label">Runtime</span>
            <span class="console-signal__value job-create__signal-value">{{ runtimeLabel }}</span>
            <span class="console-signal__note">{{ runtimeSummaryNote }}</span>
          </button>
        </div>

        <div v-if="workspaceWarning" class="alert alert--error">
          {{ workspaceWarning }}
        </div>

        <details id="job-create-workspace-section" ref="workspaceSection" class="console-module" open>
          <summary class="console-module__summary">Workspace</summary>
          <div class="job-create__stack">
            <label class="console-filter">
              <span class="console-filter__label">Filter</span>
              <input
                v-model="workspaceFilter"
                type="search"
                class="form-input"
                placeholder="Filter workspaces"
              >
            </label>
            <label class="console-filter">
              <span class="console-filter__label">Workspace</span>
              <select ref="workspaceSelect" v-model="selectedWorkspaceId" class="form-input">
                <option value="">Select a workspace</option>
                <option
                  v-for="workspace in filteredWorkspaceOptions"
                  :key="workspace.workspaceId"
                  :value="workspace.workspaceId"
                >
                  {{ workspace.name }} - {{ workspace.status }}
                </option>
              </select>
            </label>

            <p v-if="selectedWorkspaceDescription" class="job-create__hint">
              {{ selectedWorkspaceDescription }}
            </p>
            <p v-else class="job-create__hint">
              Workspace creation is not part of this screen yet. Use an existing ready workspace.
            </p>
          </div>
        </details>

        <details id="job-create-input-section" ref="inputSection" class="console-module" open>
          <summary class="console-module__summary">Input</summary>
          <div class="job-create__stack">
            <div class="job-create__mode-toggle" role="radiogroup" aria-label="Request input mode">
              <label class="job-create__mode-option">
                <input v-model="jobInputMode" type="radio" value="freeform">
                <span>Custom Spec</span>
              </label>
              <label class="job-create__mode-option">
                <input v-model="jobInputMode" type="radio" value="issue">
                <span>GitHub Issue</span>
              </label>
            </div>

            <label v-if="jobInputMode === 'freeform'" class="console-filter">
              <span class="console-filter__label">Request</span>
              <textarea
                ref="jobDescriptionField"
                v-model="jobDescription"
                class="form-input job-create__textarea"
                rows="8"
                placeholder="Describe the engineering request, constraints, and expected outcome."
              />
            </label>

            <template v-else>
              <label class="console-filter">
                <span class="console-filter__label">Issue Number</span>
                <input
                  ref="issueNumberField"
                  v-model="issueNumberInput"
                  type="number"
                  min="1"
                  step="1"
                  class="form-input"
                  placeholder="e.g. 211"
                >
              </label>

              <label class="console-filter">
                <span class="console-filter__label">Operator Notes</span>
                <textarea
                  v-model="operatorNotes"
                  class="form-input job-create__textarea"
                  rows="5"
                  placeholder="Optional operator constraints that stay secondary to the issue requirements."
                />
              </label>
            </template>
          </div>
        </details>

        <details id="job-create-runtime-section" ref="runtimeSection" class="console-module" open>
          <summary class="console-module__summary">Runtime</summary>
          <div class="job-create__stack">
            <label class="console-filter">
              <span class="console-filter__label">Runtime</span>
              <select ref="runtimeSelect" v-model="selectedRuntime" class="form-input">
                <option value="claude-code">Local Worker (Claude Code)</option>
                <option value="bedrock">Bedrock</option>
              </select>
            </label>

            <p class="job-create__hint">
              {{ runtimeDescription }}
            </p>

            <div v-if="selectedRuntime === 'claude-code'" class="job-create__setup-shell">
              <WorkerSetupInstructions :show-heading="false" />
            </div>
          </div>
        </details>

        <details class="console-module">
          <summary class="console-module__summary">Launch Contract</summary>
          <dl class="console-metric-list">
            <div class="console-metric">
              <dt>Runtime</dt>
              <dd>{{ runtimeLabel }}</dd>
            </div>
            <div class="console-metric">
              <dt>Workspace Source</dt>
              <dd>{{ selectedWorkspace?.sourceLabel || 'Select a workspace' }}</dd>
            </div>
            <div class="console-metric">
              <dt>Submission Kind</dt>
              <dd>{{ jobInputMode === 'issue' ? 'issue' : 'freeform' }}</dd>
            </div>
          </dl>
        </details>

        <div v-if="submitError" class="alert alert--error">
          {{ submitError }}
        </div>

        <div v-if="submitMessage" class="job-create__message">
          {{ submitMessage }}
        </div>

        <div class="button-row">
          <button
            type="button"
            class="button button--primary"
            :disabled="!canSubmit"
            @click="submitJob"
          >
            {{ submitting ? 'Submitting...' : 'Submit Request' }}
          </button>
        </div>
      </ConsoleListState>
    </section>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ConsoleListState from '@/components/ConsoleListState.vue'
import ConsolePollingStatus from '@/components/ConsolePollingStatus.vue'
import WorkerSetupInstructions from '@/components/WorkerSetupInstructions.vue'
import { useOrchestratorBaseUrl } from '@/composables/useOrchestratorBaseUrl.js'
import { apiErrorMessage } from '@/services/apiErrorMessage.js'
import { useFrontendLogger } from '@/services/FrontendLogger'
import { fetchWorkspaceOptions } from '@/services/workspaceOptions.js'

const route = useRoute()
const router = useRouter()
const { orchestratorBaseUrl, apiHeaders } = useOrchestratorBaseUrl()
const logger = useFrontendLogger().child('[ConsoleJobCreate]')
const WORKSPACE_REFRESH_INTERVAL_MS = 3000
const TERMINAL_WORKSPACE_STATUSES = new Set(['ready', 'failed', 'archived'])
const MAX_WORKSPACE_POLL_ERRORS = 5

const loading = ref(true)
const error = ref('')
const workspaceWarning = ref('')
const submitting = ref(false)
const submitError = ref('')
const submitMessage = ref('')

const workspaces = ref([])
const workspaceFilter = ref('')
const selectedWorkspaceId = ref('')
const jobInputMode = ref('freeform')
const jobDescription = ref('')
const issueNumberInput = ref('')
const operatorNotes = ref('')
const selectedRuntime = ref('claude-code')
const workspaceSection = ref(null)
const inputSection = ref(null)
const runtimeSection = ref(null)
const workspaceSelect = ref(null)
const jobDescriptionField = ref(null)
const issueNumberField = ref(null)
const runtimeSelect = ref(null)
const workspacePollBusy = ref(false)
let workspacePollTimer = null
let workspacePollErrorCount = 0

const workspaceOptions = computed(() => {
  return workspaces.value
    .filter((workspace) => workspace.status !== 'archived')
    .sort((a, b) => a.name.localeCompare(b.name))
})
const filteredWorkspaceOptions = computed(() => {
  const query = workspaceFilter.value.trim().toLowerCase()
  if (!query) return workspaceOptions.value
  const selected = selectedWorkspaceId.value
  return workspaceOptions.value.filter((workspace) => {
    // Always keep the active selection rendered, even when it does not match the
    // filter — otherwise the native <select> silently snaps to the empty
    // placeholder while still submitting the hidden selection.
    if (selected && workspace.workspaceId === selected) return true
    const label = `${workspace.name} - ${workspace.status}`
    return label.toLowerCase().includes(query)
  })
})
const workspacePollingActive = computed(() => workspacePollBusy.value || workspaces.value.some((workspace) => isPendingWorkspaceStatus(workspace.status)))

const selectedWorkspace = computed(() => {
  return workspaceOptions.value.find((workspace) => workspace.workspaceId === selectedWorkspaceId.value) ?? null
})

const selectedWorkspaceLabel = computed(() => selectedWorkspace.value?.name || 'Select a workspace')
const selectedWorkspaceDescription = computed(() => selectedWorkspace.value?.description || '')

const selectedWorkspaceNote = computed(() => {
  if (!selectedWorkspace.value) return 'A ready workspace is required before you can submit a request.'
  const parts = []
  if (selectedWorkspace.value.sourceLabel) parts.push(selectedWorkspace.value.sourceLabel)
  if (selectedWorkspace.value.branch) parts.push(`branch ${selectedWorkspace.value.branch}`)
  parts.push(`status ${selectedWorkspace.value.status || 'unknown'}`)
  return parts.join(' / ')
})

const selectedWorkspaceToneClass = computed(() => {
  const status = selectedWorkspace.value?.status
  if (status === 'failed') return 'console-signal--failed'
  if (status === 'assembling' || status === 'creating' || status === 'pending') return 'console-signal--pending'
  return 'console-signal--ready'
})

const submissionModeLabel = computed(() => (jobInputMode.value === 'issue' ? 'GitHub Issue' : 'Custom Spec'))

const runtimeLabel = computed(() => {
  return selectedRuntime.value === 'claude-code' ? 'Local Worker (Claude Code)' : 'Bedrock'
})

const runtimeDescription = computed(() => {
  if (selectedRuntime.value === 'claude-code') {
    return 'Uses the local worker path. Download the worker and run it alongside your local Claude Code CLI.'
  }
  return 'Uses the shared Bedrock-backed runtime path when it is configured on the server.'
})

const runtimeSummaryNote = computed(() => {
  return selectedRuntime.value === 'claude-code'
    ? 'Local worker path with download and run instructions below.'
    : 'Shared server-side runtime path.'
})

const issueNumber = computed(() => {
  const parsed = Number.parseInt(issueNumberInput.value, 10)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null
})

const canSubmit = computed(() => {
  if (loading.value || submitting.value) return false
  if (!selectedWorkspace.value) return false
  if (selectedWorkspace.value.status !== 'ready') return false
  if (jobInputMode.value === 'issue') return issueNumber.value !== null
  return jobDescription.value.trim().length > 0
})

function openSection(sectionRef, focusRef) {
  sectionRef.value?.setAttribute('open', '')
  sectionRef.value?.scrollIntoView?.({ block: 'start', behavior: 'smooth' })
  focusRef?.value?.focus?.()
}

function openWorkspaceSection() {
  openSection(workspaceSection, workspaceSelect)
}

function openInputSection() {
  openSection(inputSection, jobInputMode.value === 'issue' ? issueNumberField : jobDescriptionField)
}

function openRuntimeSection() {
  openSection(runtimeSection, runtimeSelect)
}

function normalizeWorkspace(raw) {
  const workspaceId = raw.id ?? raw.workspaceId
  const isGithub =
    raw.type === 'public-github' || raw.type === 'private-github' || raw.source === 'github'
  const sourceLabel = raw.sourceLabel || (isGithub ? 'GitHub' : (raw.type || raw.source ? 'Upload' : 'Workspace'))

  return {
    ...raw,
    workspaceId,
    name: raw.name || workspaceId,
    sourceLabel,
  }
}

function isPendingWorkspaceStatus(status) {
  return !TERMINAL_WORKSPACE_STATUSES.has(status || 'ready')
}

function stopWorkspacePolling() {
  if (workspacePollTimer !== null) {
    clearTimeout(workspacePollTimer)
    workspacePollTimer = null
  }
  workspacePollErrorCount = 0
}

function scheduleWorkspacePoll() {
  if (workspacePollTimer !== null || !workspaces.value.some((workspace) => isPendingWorkspaceStatus(workspace.status))) return

  workspacePollTimer = setTimeout(async () => {
    workspacePollTimer = null
    await pollWorkspaceOptions()
  }, WORKSPACE_REFRESH_INTERVAL_MS)
}

async function pollWorkspaceOptions() {
  workspacePollBusy.value = true
  try {
    await fetchWorkspaces({ background: true })
    workspacePollErrorCount = 0

    if (workspaces.value.some((workspace) => isPendingWorkspaceStatus(workspace.status))) {
      scheduleWorkspacePoll()
    } else {
      stopWorkspacePolling()
    }
  } catch (pollError) {
    workspacePollErrorCount += 1
    logger.warn('Failed to poll workspace options', pollError)

    if (workspacePollErrorCount < MAX_WORKSPACE_POLL_ERRORS) {
      scheduleWorkspacePoll()
    }
  } finally {
    workspacePollBusy.value = false
  }
}

async function requestJson(path, options = {}) {
  const response = await fetch(`${orchestratorBaseUrl.value}${path}`, {
    ...options,
    headers: {
      ...apiHeaders.value,
      ...(options.headers || {}),
    },
  })

  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error(apiErrorMessage(data, response.status))
  }
  return data
}

async function fetchWorkspaces({ background = false } = {}) {
  if (!background) stopWorkspacePolling()
  const raw = await fetchWorkspaceOptions(requestJson, { includeArchived: true })
  workspaces.value = raw.map(normalizeWorkspace)

  if (workspaces.value.some((workspace) => isPendingWorkspaceStatus(workspace.status))) {
    scheduleWorkspacePoll()
  } else {
    stopWorkspacePolling()
  }
}

async function loadData({ showLoading = false } = {}) {
  if (showLoading) loading.value = true
  error.value = ''
  workspaceWarning.value = ''

  try {
    await fetchWorkspaces()
  } catch (fetchError) {
    workspaces.value = []
    error.value = 'Failed to load workspaces.'
    logger.warn('Failed to fetch workspaces for request submission', fetchError)
  } finally {
    loading.value = false
  }
}

async function submitJob() {
  if (!canSubmit.value) return

  submitting.value = true
  submitError.value = ''
  submitMessage.value = ''

  try {
    const body = {
      workspaceId: selectedWorkspaceId.value,
      runtime: selectedRuntime.value,
      ...(jobInputMode.value === 'issue'
        ? {
            issueNumber: issueNumber.value,
            ...(operatorNotes.value.trim()
              ? { operatorConstraints: { notes: operatorNotes.value.trim() } }
              : {}),
          }
        : {
            description: jobDescription.value.trim(),
          }),
    }

    const data = await requestJson('/jobs', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    })

    submitMessage.value = 'Request submitted.'
    await router.push({ name: 'ConsoleJobDetail', params: { id: data.jobId } })
  } catch (submitJobError) {
    submitError.value = submitJobError.message || 'Failed to submit request.'
    logger.warn('Failed to submit request', submitJobError)
  } finally {
    submitting.value = false
  }
}

watch(
  () => route.query.workspaceId,
  (workspaceId) => {
    selectedWorkspaceId.value = typeof workspaceId === 'string' ? workspaceId : ''
  },
  { immediate: true },
)

onMounted(() => {
  loadData({ showLoading: true })
})

onUnmounted(() => {
  stopWorkspacePolling()
})
</script>

<style scoped>
.job-create__summary-strip {
  margin-bottom: 16px;
}

.job-create__stack {
  display: grid;
  gap: 16px;
}

.job-create__signal-value {
  font-size: clamp(1.15rem, 2.3vw, 1.5rem);
  line-height: 1.05;
}

.job-create__hint,
.job-create__message {
  margin: 0;
  color: var(--shell-text-muted);
  line-height: 1.6;
}

.job-create__mode-toggle {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.job-create__mode-option {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  padding: 10px 12px;
  border: 1px solid var(--shell-border);
  background: var(--shell-surface-strong);
  color: var(--shell-text);
  font-size: 0.88rem;
}

.job-create__setup-shell {
  padding: 14px 16px;
  border: 1px solid var(--shell-border);
  background: var(--shell-surface-strong);
}

.job-create__textarea {
  min-height: 140px;
  resize: vertical;
  line-height: 1.6;
}

@media (max-width: 768px) {
  .job-create__mode-toggle {
    flex-direction: column;
  }
}
</style>
